import type { MobileRedirectMessage } from '@repo/common';

import { MobileRedirectStatus, Step } from '@repo/common';
import { useEffect, useRef, useState } from 'react';
import { default as styled } from 'styled-components';

import { useMobileRedirectQrCode } from './hooks/use-mobile-redirect-qr-code';
import { useMobileRedirectStep } from './hooks/use-mobile-redirect-step';
import { Pending } from './steps/pending';
import { QrScan } from './steps/qr-scan';
import { QrScanTutorial } from './steps/qr-scan-tutorial';

import { analytics } from '@/analytics';
import { InfoModal } from '@/components/InfoModal/constants';
import { withStep } from '@/hoc/with-step';
import { useAsyncError } from '@/hooks/use-async-error';
import { useContinueOnThisDevice } from '@/hooks/use-continue-on-this-device';
import { useWebSocket } from '@/hooks/use-web-socket';
import { setWorkflowAction, toggleInfoModalAction } from '@/providers/app-provider/actions';
import { useAppContext } from '@/providers/app-provider/context';
import { postRedirectToken, postSubmitHandled } from '@/services/rivaas-api';
import * as GSC from '@/styles/GlobalStyles';
import { createRedirectToken, createRedirectUrl } from '@/utils/mobile-redirect';
import { nextWorkflowFromSubmit } from '@/utils/step-workflow';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 100%;
  row-gap: 0.25rem;
  margin: 2rem auto;
  max-width: 40.125rem;
  color: ${(p) => p.theme.colors.oxfordBlue[80]};

  @media (min-width: ${(p) => p.theme.devices.tablet}) {
    margin: 2.5rem auto;
  }
`;

export const MobileRedirect = withStep({ only: [Step.MOBILE_REDIRECT] })(({ dispatch }) => {
  const {
    state: { customerData },
  } = useAppContext();

  const [redirectTokenUpdated, setRedirectTokenUpdated] = useState(false);
  const { MobileRedirectStep, setStep, step } = useMobileRedirectStep();
  const redirectToken = useRef(createRedirectToken());
  const redirectUrl = useRef(createRedirectUrl(redirectToken.current));
  const asyncError = useAsyncError();
  const { continueOnThisDevice } = useContinueOnThisDevice();
  const canContinueOnThisDevice = customerData?.mobileRedirectSkippable !== false;

  useEffect(() => {
    analytics.redirectInit();
  }, []);

  // No extra short-url hop: show and embed the final mobile redirect URL.
  const shortUrlIsLoading = false;
  const shortUrl = redirectUrl.current;

  const { qrCode } = useMobileRedirectQrCode({ text: redirectUrl.current });

  function onWebSocketError(_event: Event) {
    asyncError('WebSocket error');
  }

  function onRedirectFinished(terminalNextStep?: { step: string }) {
    analytics.conversionCompleted();

    const redirectOptions = {
      unverifiedUrl: customerData?.redirect?.unverifiedUrl,
      verifiedUrl: customerData?.redirect?.verifiedUrl,
    };

    if (terminalNextStep?.step) {
      const workflowBase = nextWorkflowFromSubmit({ nextStep: terminalNextStep }, redirectOptions);

      if (workflowBase) {
        dispatch(setWorkflowAction(workflowBase));
      }

      return;
    }

    // Fallback: passive sync — do not advance an in-progress UI step on the desktop session.
    void (async () => {
      try {
        const data = await postSubmitHandled();
        const workflowBase = nextWorkflowFromSubmit(data, redirectOptions);

        if (workflowBase) {
          dispatch(setWorkflowAction(workflowBase));
        }
      } catch (err: unknown) {
        asyncError(`Could not advance after mobile redirect finished: ${String(err)}`);
      }
    })();
  }

  function onMobileConnected() {
    analytics.redirectSuccess();
    setStep(MobileRedirectStep.PENDING);
  }

  function onWebSocketMessage(message: string) {
    const { nextStep, status, workflow } = JSON.parse(message) as MobileRedirectMessage;

    switch (status) {
      case MobileRedirectStatus.FINISHED:
        onRedirectFinished(nextStep);

        break;
      case MobileRedirectStatus.CONNECTED:
        onMobileConnected();

        break;

      case MobileRedirectStatus.ERROR:
        asyncError('Mobile redirect error');

        break;

      case MobileRedirectStatus.EXPIRED:
        dispatch(toggleInfoModalAction(InfoModal.SESSION_EXPIRED));

        break;
    }

    if (workflow) {
      dispatch(setWorkflowAction(workflow));
    }
  }

  const { isReady, sendMessage } = useWebSocket({
    url: import.meta.env.VITE_WEBSOCKET_URL,
    onMessage: onWebSocketMessage,
    onError: onWebSocketError,
    onClose: () => asyncError('WebSocket closed'),
  });

  useEffect(() => {
    async function updateRedirectToken() {
      try {
        await postRedirectToken({ redirectToken: redirectToken.current });

        setRedirectTokenUpdated(true);
      } catch (error) {
        asyncError(`Could not update redirect token: ${error}`);
      }
    }

    updateRedirectToken();
  }, [asyncError]);

  useEffect(() => {
    if (isReady && redirectTokenUpdated) {
      sendMessage(JSON.stringify({ redirectToken: redirectToken.current }));
    }
  }, [isReady, redirectTokenUpdated, sendMessage]);

  const onBackClick = async () => {
    try {
      await continueOnThisDevice();
    } catch (error) {
      asyncError(`Could not continue on this device: ${error}`);
    }
  };

  function renderStep() {
    switch (step) {
      case MobileRedirectStep.SCAN:
        return (
          <QrScan
            isLoading={shortUrlIsLoading}
            onContinueOnThisDevice={canContinueOnThisDevice ? onBackClick : undefined}
            onTutorialClick={() => setStep(MobileRedirectStep.TUTORIAL)}
            qrCode={qrCode}
            shortUrl={shortUrl}
          />
        );
      case MobileRedirectStep.TUTORIAL:
        return <QrScanTutorial onBackClick={() => setStep(MobileRedirectStep.SCAN)} />;
      case MobileRedirectStep.PENDING:
        return <Pending isLoading={shortUrlIsLoading} qrCode={qrCode} shortUrl={shortUrl} />;
      default:
        return null;
    }
  }

  return (
    <GSC.PageWrapper>
      <Container>{renderStep()}</Container>
    </GSC.PageWrapper>
  );
});
